import type * as Yup from "yup";
import {
  UNIT_LABEL_KEYS,
  UNIT_LABEL_FALLBACKS,
  PURCHASE_PRICE_EXAMPLES,
  SALES_PRICE_EXAMPLES,
  MIN_SALES_PRICE_EXAMPLES,
  QUANTITY_EXAMPLES,
  FORBIDDEN_CHARS_DESCRIPTION_RE,
  FORBIDDEN_CHARS_RE,
  ALLOWED_PRODUCT_NAME_RE,
  SUSPICIOUS_SEQUENCES_RE,
  ALLOWED_SINGLE_CHAR_RE
} from "../ProductMFE/Products/constants/products";
import {
  ValidationResult,
  TranslateFn
} from "../ProductMFE/Products/types/products";

/**
 * Validates a product or brand name.
 *
 * Rules applied in order:
 * 1. Must not be empty / whitespace-only.
 * 2. Must not exceed maxLength.
 * 3. Must not contain forbidden characters (markup delimiters, control chars).
 * 4. Must pass the allowlist (letters, digits, safe punctuation).
 * 5. Must not contain suspicious repeated special-char sequences.
 * 6. Must include at least one letter or digit.
 */
export const validateProductName = (
  value: string,
  fieldLabel = "Product name",
  maxLength = 200
): ValidationResult => {
  const trimmed = value.trim();

  if (!trimmed) {
    return { valid: false, message: `${fieldLabel} is required.` };
  }

  if (trimmed.length > maxLength) {
    return {
      valid: false,
      message: `${fieldLabel} must be ${maxLength} characters or fewer.`
    };
  }

  if (FORBIDDEN_CHARS_RE.test(trimmed)) {
    const found = trimmed.match(FORBIDDEN_CHARS_RE)?.[0];
    // Only include the character in the message if it is printable (code point > U+001F)
    const printable =
      found && (found.codePointAt(0) ?? 0) > 31 ? ` ("${found}")` : "";
    return {
      valid: false,
      message: `${fieldLabel} contains an invalid character${printable}. Remove special markup characters like < > { } \\ |.`
    };
  }

  if (!ALLOWED_PRODUCT_NAME_RE.test(trimmed)) {
    // Use the anchor-free single-char regex to correctly find the first offender.
    const offender = [...trimmed].find(
      (ch) => !ALLOWED_SINGLE_CHAR_RE.test(ch)
    );
    return {
      valid: false,
      message: offender
        ? `${fieldLabel} contains an unexpected character: "${offender}". Use letters, numbers, spaces, and common punctuation only.`
        : `${fieldLabel} contains characters that are not allowed.`
    };
  }

  if (SUSPICIOUS_SEQUENCES_RE.test(trimmed)) {
    return {
      valid: false,
      message: `${fieldLabel} contains unusual character sequences (e.g. "&&", "??"). Please use plain text.`
    };
  }

  if (!/[\p{L}\p{N}]/u.test(trimmed)) {
    return {
      valid: false,
      message: `${fieldLabel} must include at least one letter or number.`
    };
  }

  return { valid: true };
};

/**
 * Validates a description field.
 * More permissive than names: allows newlines and tabs for multiline inputs,
 * but still blocks raw markup characters and non-printable control characters.
 */
export const validateDescription = (
  value: string,
  fieldLabel = "Description",
  maxLength = 2000
): ValidationResult => {
  const trimmed = value.trim();

  if (!trimmed) {
    return { valid: false, message: `${fieldLabel} is required.` };
  }

  if (trimmed.length > maxLength) {
    return {
      valid: false,
      message: `${fieldLabel} must be ${maxLength} characters or fewer.`
    };
  }

  if (FORBIDDEN_CHARS_DESCRIPTION_RE.test(trimmed)) {
    return {
      valid: false,
      message: `${fieldLabel} contains invalid characters. Remove markup characters like < > { } \\ |.`
    };
  }

  return { valid: true };
};

// Validates short label
export const validateShortLabel = (
  value: string,
  fieldLabel = "Field",
  maxLength = 150
): ValidationResult => {
  // Defensive guard: protect against accidental non-string call-site usage
  if (typeof value !== "string" || !value.trim()) {
    return { valid: false, message: `${fieldLabel} is required.` };
  }

  const trimmed = value.trim();

  if (trimmed.length > maxLength) {
    return {
      valid: false,
      message: `${fieldLabel} must be ${maxLength} characters or fewer.`
    };
  }

  if (FORBIDDEN_CHARS_RE.test(trimmed)) {
    return {
      valid: false,
      message: `${fieldLabel} contains invalid characters. Remove special markup characters like < > { } \\ |.`
    };
  }

  if (!/[\p{L}\p{N}]/u.test(trimmed)) {
    return {
      valid: false,
      message: `${fieldLabel} must include at least one letter or number.`
    };
  }

  return { valid: true };
};

export const productNameYupTest = (fieldLabel = "Product name") => ({
  name: "product-name-chars",
  test(
    this: Yup.TestContext,
    value: string | undefined
  ): boolean | Yup.ValidationError {
    if (!value) return true;
    const result = validateProductName(value, fieldLabel);
    if (result.valid) return true;
    return this.createError({ message: result.message });
  }
});

export const descriptionYupTest = (fieldLabel = "Description") => ({
  name: "description-chars",
  test(
    this: Yup.TestContext,
    value: string | undefined
  ): boolean | Yup.ValidationError {
    if (!value) return true;
    const result = validateDescription(value, fieldLabel);
    if (result.valid) return true;
    return this.createError({ message: result.message });
  }
});

export const shortLabelYupTest = (fieldLabel = "Field", maxLength = 150) => ({
  name: "short-label-chars",
  test(
    this: Yup.TestContext,
    value: string | undefined
  ): boolean | Yup.ValidationError {
    if (!value) return true;
    const result = validateShortLabel(value, fieldLabel, maxLength);
    if (result.valid) return true;
    return this.createError({ message: result.message });
  }
});

// ---------------------------------------------------------------------------
// Unit label helpers
// ---------------------------------------------------------------------------

export type BaseUnitType =
  | "UNIT"
  | "KG"
  | "G"
  | "L"
  | "ML"
  | "M"
  | "CM"
  | "MM"
  | "SQCM"
  | "OZ"
  | "LB"
  | "BOTTLE"
  | string
  | undefined
  | null;

/**
 * Maps a GraphQL BaseUnit enum value to a translated human-readable label.
 * Falls back to the lowercased raw value for unknown units.
 *
 * Examples: "KG" → "kilograms", "L" → "liters", "BOTTLE" → "bottles"
 */
export const getUnitLabel = (unit: BaseUnitType, t: TranslateFn): string => {
  if (!unit) return t("units-unit", UNIT_LABEL_FALLBACKS.UNIT);
  const normalized = unit.toUpperCase();
  const key = UNIT_LABEL_KEYS[normalized];
  const fallback = UNIT_LABEL_FALLBACKS[normalized] ?? unit.toLowerCase();
  return key ? t(key, fallback) : fallback;
};

// ---------------------------------------------------------------------------
// Placeholder helpers
// ---------------------------------------------------------------------------

/**
 * Returns a contextual placeholder for the purchase price field.
 *
 * Examples:
 *   UNIT   → "e.g. 1500 per Unit"
 *   KG     → "e.g. 800 per kg"
 *   BOTTLE → "e.g. 2500 per Bottle"
 */
export const getPurchasePricePlaceholder = (
  unit: BaseUnitType,
  t: TranslateFn
): string => {
  const key = unit ? (UNIT_LABEL_KEYS[unit.toUpperCase()] ?? null) : null;
  const label = getUnitLabel(unit, t);
  const price = (key ? PURCHASE_PRICE_EXAMPLES[key] : null) ?? 1000;
  return `e.g. ${price} per ${label}`;
};

/**
 * Returns a contextual placeholder for the sales price field.
 * Example values sit above the purchase price to reflect a realistic margin.
 */
export const getSalesPricePlaceholder = (
  unit: BaseUnitType,
  t: TranslateFn
): string => {
  const key = unit ? (UNIT_LABEL_KEYS[unit.toUpperCase()] ?? null) : null;
  const label = getUnitLabel(unit, t);
  const price = (key ? SALES_PRICE_EXAMPLES[key] : null) ?? 1500;
  return `e.g. ${price} per ${label}`;
};

/**
 * Returns a contextual placeholder for the minimum sales price field.
 * Example values sit between purchase and sales price as a realistic floor.
 */
export const getMinSalesPricePlaceholder = (
  unit: BaseUnitType,
  t: TranslateFn
): string => {
  const key = unit ? (UNIT_LABEL_KEYS[unit.toUpperCase()] ?? null) : null;
  const label = getUnitLabel(unit, t);
  const price = (key ? MIN_SALES_PRICE_EXAMPLES[key] : null) ?? 1200;
  return `e.g. ${price} per ${label}`;
};

/**
 * Returns a contextual placeholder for the total quantity field.
 */
export const getTotalQuantityPlaceholder = (
  unit: BaseUnitType,
  t: TranslateFn
): string => {
  const key = unit ? (UNIT_LABEL_KEYS[unit.toUpperCase()] ?? null) : null;
  const fallback = `e.g. 10 ${getUnitLabel(unit, t)}`;
  return (key ? QUANTITY_EXAMPLES[key] : null) ?? fallback;
};
